import { escapePanelAttribute } from "@productlane/lib/src/tiptap/panel-markdown"

import type { MdxBlockReader } from "./registry"

// Attributes may hold `>` inside quotes or braces (actions={["copy"]}).
const ATTRIBUTES = String.raw`((?:\s+[\w-]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|\{[^}]*\}))?)*)`
const PROMPT_BLOCK = new RegExp(
	String.raw`<Prompt${ATTRIBUTES}\s*>([\s\S]*?)<\/Prompt>`,
	"g",
)

export const decodeAttribute = (value: string): string =>
	value
		.replace(/&quot;/g, '"')
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&amp;/g, "&")

export function readJsxAttribute(
	attributes: string,
	name: string,
): string | null {
	const match = new RegExp(
		String.raw`(?:^|\s)${name}\s*=\s*(?:"([^"]*)"|'([^']*)'|\{([^}]*)\})`,
	).exec(attributes)
	if (!match) return null
	const quoted = match[1] ?? match[2]
	if (quoted !== undefined) return decodeAttribute(quoted)
	const expression = (match[3] ?? "").trim()
	// description={"..."} is the same string in braces.
	const literal = /^["'`]([\s\S]*)["'`]$/.exec(expression)?.[1]
	return literal ?? expression
}

export function readPromptActions(attributes: string): string {
	const raw = readJsxAttribute(attributes, "actions") ?? ""
	const list = [...raw.matchAll(/["'](copy|cursor)["']/g)].map((m) => m[1])
	const unique = [...new Set(list)]
	return unique.length > 0 ? unique.join(",") : "copy"
}

function dedentBody(body: string): string {
	const lines = body
		.replace(/^\s*\n/, "")
		.replace(/\s+$/, "")
		.split("\n")
	const indents = lines
		.filter((line) => line.trim())
		.map((line) => /^[ \t]*/.exec(line)?.[0].length ?? 0)
	const shift = indents.length ? Math.min(...indents) : 0
	return lines.map((line) => line.slice(shift)).join("\n")
}

const escapeText = (value: string): string =>
	value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

// The description is one line of markdown, so its paragraph wrapper goes.
const unwrapParagraph = (html: string): string =>
	html
		.trim()
		.replace(/^<p>([\s\S]*)<\/p>$/, "$1")
		.trim()

export const promptReaders: MdxBlockReader[] = [
	{
		name: "prompt",
		pattern: PROMPT_BLOCK,
		render: (match, parseBody) => {
			const attributes = match[1] ?? ""
			const description = readJsxAttribute(attributes, "description") ?? ""
			const icon = readJsxAttribute(attributes, "icon")
			const actions = readPromptActions(attributes)
			const descriptionHtml = description.trim()
				? unwrapParagraph(parseBody(description))
				: ""
			const text = dedentBody(match[2] ?? "")
			return `<div data-type="prompt" data-actions="${actions}"${
				icon ? ` data-icon="${escapePanelAttribute(icon)}"` : ""
			}><p data-prompt-description>${descriptionHtml}</p><pre data-prompt-text>${escapeText(
				text,
			)}</pre></div>`
		},
	},
]
